import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, ChevronDown, Menu, X } from 'lucide-react';
import { navLinks } from '../data/mock';
import { useScrolled } from '../hooks/useScrollAnimation';

const Navbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navRef = useRef(null);
  const scrolled = useScrolled(20);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <nav
      ref={navRef}
      className={`bg-white sticky top-0 z-50 border-b border-border ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
      style={{ transition: 'box-shadow 0.3s ease' }}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-[68px]">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 group">
            <img 
              src="/logo.png" 
              alt="B2BTechLeads" 
              className="h-10 w-auto"
            />
            <div className="flex items-center ml-2">
              <span
                style={{ color: '#43a047' }}
                className="font-bold text-[22px] leading-none tracking-tight"
                >B2B</span>
              <span className="font-bold text-[22px] leading-none text-foreground mx-0.5">TECH</span>
              <span
                style={{ color: '#43a047' }}
                className="font-bold text-[22px] leading-none tracking-tight"
                >LEADS</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center">
            {navLinks.map((link) => (
              <div
                key={link.href}
                className="relative"
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.dropdown ? (
                  <button
                    className={`flex items-center gap-0.5 px-3.5 h-[68px] text-sm font-medium border-b-2 ${
                      isActive(link.href)
                        ? 'text-[#43a047] border-[#43a047]'
                        : 'text-muted-foreground border-transparent hover:text-[#43a047] hover:border-[#43a047]'
                    }`}
                  >
                    {link.label}
                    <ChevronDown
                      size={12}
                      className={`mt-0.5 ${activeDropdown === link.label ? 'rotate-180' : ''}`}
                      style={{ transition: 'transform 0.2s ease' }}
                    />
                  </button>
                ) : (
                  <Link
                    to={link.href}
                    className={`flex items-center px-3.5 h-[68px] text-sm font-medium border-b-2 ${
                      isActive(link.href)
                        ? 'text-[#43a047] border-[#43a047]'
                        : 'text-muted-foreground border-transparent hover:text-[#43a047] hover:border-[#43a047]'
                    }`}
                  >
                    {link.label}
                  </Link>
                )}

                {/* Dropdown */}
                {link.dropdown && activeDropdown === link.label && (
                  <div className="dropdown-enter absolute top-full left-0 w-56 bg-white shadow-xl border border-border z-[100] rounded-b-md overflow-hidden">
                    {link.dropdown.map((item, idx) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        style={{ animationDelay: `${idx * 0.04}s` }}
                        className="anim-fade-in flex items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground hover:text-white hover:bg-[#43a047] border-b border-gray-50 last:border-0 group/item"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#43a047] group-hover/item:bg-white flex-shrink-0" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Search */}
            <div className="relative ml-2">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 text-muted-foreground hover:text-[#43a047]"
              >
                <Search size={16} />
              </button>
              {searchOpen && (
                <div className="dropdown-enter absolute right-0 top-full mt-1 bg-white shadow-lg border border-border z-[100] rounded-md overflow-hidden">
                  <form onSubmit={(e) => e.preventDefault()} className="flex items-center">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search..."
                      autoFocus
                      className="px-3 py-2 text-sm outline-none w-52 border-0"
                    />
                    <button type="submit" className="p-2 text-muted-foreground hover:text-[#43a047]">
                      <Search size={14} />
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 text-muted-foreground hover:text-[#43a047]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <span style={{ transition: 'transform 0.2s ease', display: 'block', transform: mobileOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </span>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="anim-slide-down lg:hidden border-t border-border py-2 pb-4">
            {navLinks.map((link) => (
              <div key={link.href}>
                {link.dropdown ? (
                  <>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === link.label ? null : link.label)}
                      className="flex items-center justify-between w-full px-4 py-2.5 text-sm text-muted-foreground hover:text-[#43a047]"
                    >
                      {link.label}
                      <ChevronDown
                        size={14}
                        style={{ transition: 'transform 0.2s ease', transform: mobileExpanded === link.label ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </button>
                    {mobileExpanded === link.label && (
                      <div className="anim-slide-down bg-muted pl-8">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="block py-2 px-4 text-sm text-muted-foreground hover:text-[#43a047]"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.href}
                    className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-[#43a047]"
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
